import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { arrayUnion, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { withTenant } from '../services/data';
import { colors, fonts, radius } from './theme';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const ALERT_WINDOW_MS = 60 * 60 * 1000; // 60 minutes

/**
 * Returns minutes until an event starts. For all-day events (YYYY-MM-DD),
 * treat midnight local time as the start.
 */
function minutesUntil(start, isAllDay) {
  const now = Date.now();
  let startMs;
  if (isAllDay) {
    const [y, m, d] = start.split('-').map(Number);
    startMs = new Date(y, m - 1, d).getTime();
  } else {
    startMs = new Date(start).getTime();
  }
  return Math.round((startMs - now) / 60000);
}

export default function EventAlertBanner({ user, onViewEvent, onVisibilityChange }) {
  const [events, setEvents] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef(null);

  // RW-69: subscribe to the persisted dismiss list on the user doc so the banner
  // stays gone after a refresh / next poll. Both the X button (below) and the
  // RSVP flow in EventsPage write to dismissedEventBanners via arrayUnion.
  useEffect(() => {
    if (!user?.uid) {
      setDismissed(new Set());
      return undefined;
    }
    const ref = doc(db, 'users', user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const list = snap.exists() ? (snap.data()?.dismissedEventBanners || []) : [];
        setDismissed(new Set(list));
      },
      () => {},
    );
    return () => unsub();
  }, [user?.uid]);

  const fetchEvents = useCallback(async () => {
    if (!user) return;
    try {
      const fns = getFunctions();
      const getCalendarEvents = httpsCallable(fns, 'getCalendarEvents');
      const result = await getCalendarEvents();
      setEvents(result.data.events || []);
    } catch {
      // silent — banner is non-critical
    }
  }, [user]);

  // Initial fetch + poll every 5 minutes
  useEffect(() => {
    if (!user) return;
    fetchEvents();
    timerRef.current = setInterval(fetchEvents, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [user, fetchEvents]);

  // Auto-refresh the "X minutes" display every 30s and auto-dismiss past events
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // Events starting within 60 minutes, not dismissed, not yet past
  // tick dependency ensures recomputation every 30s for auto-dismiss and countdown
  const alertEvents = useMemo(() => {
    void tick;
    return events
      .filter((e) => {
        if (dismissed.has(e.id)) return false;
        const mins = minutesUntil(e.start, e.isAllDay);
        return mins > 0 && mins <= (ALERT_WINDOW_MS / 60000);
      })
      .sort((a, b) => minutesUntil(a.start, a.isAllDay) - minutesUntil(b.start, b.isAllDay));
  }, [events, dismissed, tick]);

  // Keep activeIndex in bounds
  useEffect(() => {
    if (activeIndex >= alertEvents.length) setActiveIndex(0);
  }, [activeIndex, alertEvents.length]);

  // Notify parent when visibility changes
  const isVisible = alertEvents.length > 0;
  const prevVisible = useRef(false);
  useEffect(() => {
    if (isVisible !== prevVisible.current) {
      prevVisible.current = isVisible;
      onVisibilityChange?.(isVisible);
    }
  }, [isVisible, onVisibilityChange]);

  const handleDismiss = useCallback(async (eventId) => {
    // Optimistic local update so the banner disappears immediately even if the
    // Firestore write is slow / offline.
    setDismissed((prev) => new Set(prev).add(eventId));
    if (!user?.uid || !eventId) return;
    try {
      await setDoc(
        doc(db, 'users', user.uid),
        withTenant('users', { dismissedEventBanners: arrayUnion(eventId) }),
        { merge: true },
      );
    } catch {
      // Non-critical — the local set still hides the banner for this session.
    }
  }, [user?.uid]);

  if (!isVisible) return null;

  const current = alertEvents[activeIndex] || alertEvents[0];
  const mins = minutesUntil(current.start, current.isAllDay);

  return (
    <div style={{
      position: 'fixed', top: 'var(--reve-safe-top)', left: 0, right: 0, zIndex: 1100,
      height: 48, background: colors.coral, color: colors.white,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: fonts.body, fontSize: 13, fontWeight: 500,
      padding: '0 48px 0 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      gap: 12,
    }}>
      {/* Multiple-event indicator */}
      {alertEvents.length > 1 ? (
        <button
          type="button"
          onClick={() => setActiveIndex((i) => (i + 1) % alertEvents.length)}
          style={{
            background: 'rgba(255,255,255,0.22)', border: 'none', borderRadius: 10,
            color: colors.white, fontSize: 11, fontWeight: 600, padding: '2px 8px',
            cursor: 'pointer', fontFamily: fonts.body, flexShrink: 0,
            letterSpacing: '0.02em',
          }}
          aria-label="Show next event"
        >
          {activeIndex + 1} of {alertEvents.length}
        </button>
      ) : null}

      {/* Event info */}
      <span style={{
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        flex: 1, textAlign: 'center', minWidth: 0,
      }}>
        🗓 <strong>{current.title}</strong> starts in {mins} minute{mins !== 1 ? 's' : ''}
        {current.location ? ` — ${current.location}` : ''}
      </span>

      {/* View Event button */}
      <button
        type="button"
        onClick={() => onViewEvent(current)}
        style={{
          background: colors.white, color: colors.coralInk, border: 'none',
          borderRadius: radius.sm, padding: '4px 12px', fontSize: 12,
          fontWeight: 600, fontFamily: fonts.body, cursor: 'pointer',
          flexShrink: 0, whiteSpace: 'nowrap',
        }}
      >
        View Event
      </button>

      {/* Dismiss button */}
      <button
        type="button"
        onClick={() => handleDismiss(current.id)}
        style={{
          position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)',
          fontSize: 16, cursor: 'pointer', padding: '4px 6px', lineHeight: 1,
        }}
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}
